import React, { useEffect, useMemo, useState } from 'react'
import { Spinner } from 'react-bootstrap'
import { ExplainedResult } from './models'
import UserWithAvatar from '../../common/user/UserWithAvatar'
import { useAppDispatch, useAppSelector } from '../../../../../store/hooks'
import {
  selectResultExplainerAiFeatureId,
  selectResultExplainerIsLoading,
  selectResultExplainerResult,
  selectResultExplainerResultContext,
  setResultExplainerResultContext,
} from '../../../../../store/meetingSummary/meetingSupport'
import { selectAiFeatures } from '../../../../../store/meetingSummary/aiFeatures'
import { selectAiFeaturePresets } from '../../../../../store/meetingSummary/aiFeaturePresets'
import { selectRoomSession } from '../../../../../store/meetingSummary/roomSession'
import { useExplainResult } from '../hooks/api'

import './ResultExplainer.scss'

export const ResultExplainer: React.FC = () => {
  const aiFeatureId = useAppSelector(selectResultExplainerAiFeatureId)
  const resultToExplain = useAppSelector(selectResultExplainerResult)
  const roomSession = useAppSelector(selectRoomSession)
  const aiFeatures = useAppSelector(selectAiFeatures)
  const aiFeaturePresets = useAppSelector(selectAiFeaturePresets)
  const isExplainResultLoading = useAppSelector(selectResultExplainerIsLoading)
  const resultContext = useAppSelector(selectResultExplainerResultContext)
  const dispatch = useAppDispatch()

  const explainResult = useExplainResult()

  const [explanationResult, setExplanationResult] = useState<ExplainedResult | null>(null)

  const aiFeaturePresetDescription = useMemo(() => {
    const aiFeature = aiFeatures.find((aiFeature) => aiFeature.id === aiFeatureId)
    const aiFeaturePreset = aiFeaturePresets.find((aiFeaturePreset) => aiFeaturePreset.id === aiFeature?.preset?.id)
    return aiFeaturePreset?.description ?? ''
  }, [aiFeatureId, aiFeatures, aiFeaturePresets])

  useEffect(() => {
    if (!resultToExplain) {
      return
    }
    if (aiFeaturePresetDescription) {
      dispatch(setResultExplainerResultContext(aiFeaturePresetDescription))
    }
    const executeExplainResult = async () => {
      const explanationResult = await explainResult(resultToExplain, resultContext ?? '')
      setExplanationResult(explanationResult)
    }
    executeExplainResult()
  }, [aiFeaturePresetDescription, dispatch, explainResult, resultContext, resultToExplain])

  if (isExplainResultLoading) {
    return (
      <div className='d-flex align-items-center justify-content-center mt-5'>
        <Spinner />
      </div>
    )
  }

  return (
    <div className='d-flex flex-column gap-2 zocks-py-1.25 zocks-px-1.25'>
      <div className='lead-extra-small'>Result to explain</div>
      <div className='d-flex flex-column p-3 result-to-explain-container'>{resultToExplain}</div>
      <div className='lead-extra-small'>Explanation</div>
      <div className='d-flex gap-2 p-3 explanation-container'>
        <i className='bi bi-stars zocks-text-primary' />
        <div className='paragraph-medium'>{explanationResult ? explanationResult.explanation : 'Explanation cannot be determined at this time'}</div>
      </div>
      {explanationResult && roomSession ? (
        <div className='mt-4 d-flex flex-column gap-4'>
          {explanationResult.transcript.map((explanationTranscript, idx) => {
            const { userId, content } = explanationTranscript
            const { users } = roomSession
            const user = users.find((user) => user.id === userId)
            const userName = user ? user.name : 'Unknown'
            return (
              <div key={`${userId}-${idx}`} className='d-flex flex-column gap-2'>
                <div className='d-flex align-items-center gap-2 result-explainer-user'>
                  <UserWithAvatar name={userName} displayName={false} />
                  <div className='paragraph-medium bold'>{userName}</div>
                </div>
                <div className='d-flex p-3 gap-2 result-explainer-transcript-container'>
                  <i className='bi bi-quote' />
                  <div className='paragraph-medium'>{content}</div>
                </div>
              </div>
            )
          })}
        </div>
      ) : null}
    </div>
  )
}
